import { useState, useEffect, useRef } from "react";
import "./MonteSuaEspora.css";
import catalogorepique from "../../assets/images/catalogorepique.pdf";
import toast from "react-hot-toast";

// Componente para renderizar PDF com PDF.js
function PDFViewer({ pdfUrl }) {
  const containerRef = useRef(null);
  const renderTaskRef = useRef(null);
  const resizeTimeoutRef = useRef(null);
  const lastContainerWidthRef = useRef(0);
  const isRenderingRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Inicializar PDF.js
    const initPDF = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Aguardar um frame para garantir que o DOM está pronto
        await new Promise((resolve) => requestAnimationFrame(resolve));

        // Carregar os scripts do PDF.js via CDN
        if (!window.pdfjsLib) {
          await new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src =
              "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        if (window.pdfjsLib) {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
          await renderPDF(pdfUrl);
        }
      } catch (err) {
        console.error("Erro ao carregar PDF.js:", err);
        setError(
          "Erro ao carregar o PDF. Por favor, tente novamente ou abra em nova guia.",
        );
        setIsLoading(false);
      }
    };

    initPDF();

    const handleResize = () => {
      if (!containerRef.current || !containerRef.current.innerHTML) {
        return;
      }

      const currentWidth =
        containerRef.current.offsetWidth ||
        containerRef.current.parentElement?.offsetWidth ||
        0;

      if (currentWidth === lastContainerWidthRef.current) {
        return;
      }

      if (resizeTimeoutRef.current) {
        window.clearTimeout(resizeTimeoutRef.current);
      }

      resizeTimeoutRef.current = window.setTimeout(() => {
        renderPDF(pdfUrl);
      }, 150);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeTimeoutRef.current) {
        window.clearTimeout(resizeTimeoutRef.current);
      }
      if (renderTaskRef.current?.cancel) {
        renderTaskRef.current.cancel();
      }
    };
  }, [pdfUrl]);

  const renderPDF = async (url) => {
    if (isRenderingRef.current) {
      return;
    }

    try {
      isRenderingRef.current = true;
      setIsLoading(true);
      setError(null);

      const { pdfjsLib } = window;
      if (!pdfjsLib) {
        throw new Error("PDF.js não carregou");
      }

      const pdf = await pdfjsLib.getDocument(url).promise;
      const container = containerRef.current;

      if (!container) {
        console.error("Container ref não encontrado");
        return;
      }

      container.innerHTML = "";
      const containerWidth =
        container.offsetWidth || container.parentElement?.offsetWidth || 0;
      lastContainerWidthRef.current = containerWidth;

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        try {
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1 });

          const width = containerWidth > 0 ? containerWidth : viewport.width;
          const scale = width / viewport.width;
          const devicePixelRatio = window.devicePixelRatio || 1;
          const scaledViewport = page.getViewport({
            scale: scale * devicePixelRatio,
          });

          const canvas = document.createElement("canvas");
          canvas.width = scaledViewport.width;
          canvas.height = scaledViewport.height;
          canvas.style.width = width + "px";
          canvas.style.height = scaledViewport.height / devicePixelRatio + "px";
          canvas.style.display = "block";
          canvas.style.marginBottom = "1rem";
          canvas.style.borderRadius = "4px";
          canvas.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";

          const context = canvas.getContext("2d");
          const renderContext = {
            canvasContext: context,
            viewport: scaledViewport,
          };

          if (renderTaskRef.current?.cancel) {
            renderTaskRef.current.cancel();
          }

          renderTaskRef.current = page.render(renderContext);
          await renderTaskRef.current.promise;
          container.appendChild(canvas);
        } catch (pageErr) {
          console.error(`Erro ao renderizar página ${pageNum}:`, pageErr);
        }
      }

      setIsLoading(false);
    } catch (err) {
      console.error("Erro ao renderizar PDF:", err);
      setError(
        "Erro ao renderizar o PDF. Por favor, tente novamente ou abra em nova guia.",
      );
      setIsLoading(false);
    } finally {
      isRenderingRef.current = false;
      renderTaskRef.current = null;
    }
  };

  return (
    <div>
      {isLoading && (
        <div className="pdf-loading">
          <div className="pdf-spinner"></div>
          <p>Carregando catálogo...</p>
        </div>
      )}

      {error && (
        <div className="pdf-error">
          <p>{error}</p>
        </div>
      )}

      <div
        ref={containerRef}
        className="pdf-canvas-container"
        style={{ minHeight: isLoading ? "600px" : "auto" }}
      ></div>

      {/* Botão para abrir em nova guia */}
      <div className="pdf-actions">
        <a
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-abrir-pdf"
        >
          Abrir catálogo em nova guia
        </a>
      </div>
    </div>
  );
}

export default function SuaEspora() {
  const [corpo, setCorpo] = useState("");
  const [papagaio, setPapagaio] = useState("");
  const [roseta, setRoseta] = useState("");
  const [inicial, setInicial] = useState("");

  const enviarWhatsApp = () => {
    if (!corpo || !papagaio || !roseta) {
      toast.error("Selecione todos os componentes!");
      return;
    }

    const numero = "5554996525074";

    const mensagem = `Olá! Quero montar uma espora personalizada:

Corpo: ${corpo}${inicial ? " + " + inicial : ""}
Papagaio: ${papagaio}
Roseta: ${roseta}

Pode me ajudar com o pedido?`;

    const url = `https://wa.me/${numero}?text=${encodeURIComponent(mensagem)}`;
    window.open(url, "_blank");
  };

  const duvidaWhatsApp = () => {
    const numero = "5554996525074";
    const mensagem =
      "Olá! Gostaria de saber mais informações sobre como posso montar minha espora personalizada. ";

    const url = `https://wa.me/${numero}?text=${encodeURIComponent(mensagem)}`;
    window.open(url, "_blank");
  };

  return (
    <section className="pagina-suaespora">
      {/* HERO */}
      <div className="suaespora-hero">
        <span className="secao-etiqueta">Monte sua Espora</span>
        <h2>Crie sua espora personalizada</h2>
        <p>Consulte as opções no catálogo abaixo e selecione os componentes.</p>
      </div>

      {/* JANELA DO PDF */}
      <div className="container-pdf">
        <PDFViewer pdfUrl={catalogorepique} />
      </div>

      {/* CONFIGURADOR (Permanece igual) */}
      <div className="montador">
        <h2>Selecione os Códigos</h2>
        {/* ... restante do seu formulário grid, resumo e botão ... */}
        <div className="montador-grid">
          <select onChange={(e) => setCorpo(e.target.value)}>
            <option value="">Escolha o Corpo</option>
            <option value="C001">C001</option>
            <option value="C002">C002</option>
            <option value="C003">C003</option>
            <option value="C007">C007</option>
            <option value="C008">C008</option>
            <option value="C009">C009</option>
            <option value="C010">C010</option>
            <option value="C011">C011</option>
            <option value="C012">C012</option>
          </select>

          <select onChange={(e) => setPapagaio(e.target.value)}>
            <option value="">Escolha o Papagaio</option>
            <option value="P001">P001</option>
            <option value="P002">P002</option>
            <option value="P004">P004</option>
            <option value="P005">P005</option>
            <option value="P006">P006</option>
            <option value="P007">P007</option>
            <option value="P008">P008</option>
          </select>

          <select onChange={(e) => setRoseta(e.target.value)}>
            <option value="">Escolha a Roseta</option>
            <option value="R001">R001</option>
            <option value="R002">R002</option>
            <option value="R003">R003</option>
            <option value="R006">R006</option>
            <option value="R007">R007</option>
            <option value="R008">R008</option>
          </select>

          <input
            type="text"
            placeholder="Inicial (opcional) Ex: T"
            maxLength={1}
            value={inicial}
            onChange={(e) => setInicial(e.target.value.toUpperCase())}
          />
        </div>

        <div className="resumo">
          <h3>Seu pedido:</h3>
          <p>
            {corpo || "___"} {inicial && `+ ${inicial}`} | {papagaio || "___"} |{" "}
            {roseta || "___"}
          </p>
          <p className="preco">R$ 350 + frete</p>
        </div>

        <button className="btn-montar-espora" onClick={enviarWhatsApp}>
          Finalizar no WhatsApp
        </button>
      </div>

      {/* NOTAS FINAIS */}
      <div className="suaespora-note">
        <h4>Informações importantes</h4>
        <ul>
          <li>Adicione + letra para personalização (Ex: C002 + T)</li>
          <li>Modelos infantis usam código com "i"</li>
          <li>Roseta infantil é padrão R004i por segurança</li>
          <li>Todos os modelos acompanham tento e proteção</li>
        </ul>
      </div>
    </section>
  );
}
